import {
  ArrowLeft as ArrowLeftIcon,
  Library as LibraryIcon,
  ThumbsDown as ThumbsDownIcon,
  ThumbsUp as ThumbsUpIcon,
} from 'lucide-react'
import {
  useCallback,
  useState,
} from 'react'
import Link from 'next/link'

import { analytics } from 'utils/analytics'
import { saveUserFeedback } from 'utils/analytics/feedback'
import Button from 'components/Button'
import Text from 'components/typography/Text'
import Textarea from 'components/Textarea'

type Rating = 'up' | 'down'
type RatingStep = 'feedback' | 'email'

export interface Props {
  guidePath: string
  onBackClick: (e: any) => void
}

function GuideRating({
  guidePath,
  onBackClick,
}: Props) {
  const [rating, setRating] = useState<Rating>()
  const [email, setEmail] = useState('')
  const [feedback, setFeedback] = useState('')
  const [isFeedbackSent, setIsFeedbackSent] = useState(false)

  const sendFeedback = useCallback(async () => {
    if (!feedback) return

    saveUserFeedback({
      rating,
      guide: guidePath,
      email: email || undefined,
    }, feedback)

    analytics.track('guide feedback sent', {
      rating,
      feedback,
      email: email || undefined,
    })
    analytics.track('user feedback email submitted (final guide rating)', {
      rating,
      feedback,
      email: email || undefined,
    })

    setIsFeedbackSent(true)
  }, [
    guidePath,
    rating,
    feedback,
    email,
  ])

  const rate = useCallback((rating: Rating) => {
    setRating(rating)

    saveUserFeedback({
      rating,
      guide: guidePath,
    })

    analytics.track('guide rated', { rating })
  }, [guidePath])

  return (
    <div className="
      flex-1
      flex
      flex-col
      p-4
      items-center
      justify-center
      bg-background-dots
      bg-cover
    ">
      <div className="
        relative
        bottom-[150px]
        flex
        flex-col
        items-center
        space-y-4
      ">
        <div className="
          py-6
          px-8
          flex
          flex-col
          space-y-6
          rounded-lg
          bg-white
          border-2
          border-indigo-100
        ">
          {rating && isFeedbackSent &&
            <div className="p-4">
              <Text
                className="text-center"
                size={Text.size['2xl']}
                text="We received your feedback, thank you!"
                typeface={Text.typeface.BarlowSemibold}
              />
            </div>
          }
          {rating && !isFeedbackSent &&
            <div className="
              max-w-3xl
              min-w-[400px]
              justify-center
              flex
              flex-col
              items-center
              space-y-4
            ">
              <Text
                className="text-center"
                size={Text.size['2xl']}
                text="Thank you for your feedback"
                typeface={Text.typeface.BarlowSemibold}
              />
              <Text
                className="text-center"
                text={rating === 'up' ? 'What else could we improve?' : 'Please tell us what you didn\'t like.'}
                typeface={Text.typeface.InterRegular}
              />
              <Textarea
                className="
                  border-indigo-300
                  bg-white
                  focus:border-indigo-600
                  placeholder:text-gray-500
                "
                placeholder="Your feedback..."
                value={feedback}
                autofocus
                onChange={(e) => setFeedback(e.target.value)}
              />
              <Text
                text="Would you mind sharing your email?"
              />
              <input
                className="
                  p-2
                  text-sm
                  w-full
                  rounded-lg
                  resize-none
                  border-indigo-300
                  bg-white
                  border
                  focus:border-indigo-600
                  placeholder:text-gray-500
                  outline-none
                "
                placeholder="Your email"
                value={email}
                autoFocus
                onChange={e => setEmail(e.target.value)}
                onKeyDown={e => {
                  if (e.key === 'Enter') {
                    sendFeedback()
                  }
                }}
              />
              <Button
                text="Send"
                version={feedback.length > 0 ? Button.version.PrimaryIndigo : Button.version.Disabled}
                onClick={sendFeedback}
                onEnter={sendFeedback}
              />
            </div>
          }

          {!rating &&
            <>
              <div className="
                flex
                flex-col
                space-y-2
              ">
                <Text
                  className="text-center"
                  size={Text.size['2xl']}
                  text="Rate the guide"
                  typeface={Text.typeface.BarlowSemibold}
                />
                <Text
                  className="text-center"
                  size={Text.size.base}
                  text="How was the guide that you just finished?"
                  typeface={Text.typeface.InterRegular}
                />
              </div>
              <div className="
                flex
                space-x-8
                justify-center
              ">
                <div className="
                  flex
                  flex-col
                  space-y-1
                  group
                  cursor-pointer
                  items-center
                "
                onClick={() => rate('up')}
                >
                  <ThumbsUpIcon
                    className={`
                      text-green-500
                      group-hover:scale-105
                      transition-all
                      ease-in-out
                      duration-300
                    `}
                    size={28}
                  />
                  <Text
                    color="text-green-500"
                    text="Great"
                    typeface={Text.typeface.InterBold}
                  />
                </div>
                <div className="
                  flex
                  flex-col
                  space-y-1
                  group
                  cursor-pointer
                  items-center
                "
                onClick={() => rate('down')}
                >
                  <ThumbsDownIcon
                    className="
                      text-red-500
                      group-hover:scale-105
                      transition-all
                      ease-in-out
                      duration-300
                    "
                    size={28}
                  />
                  <Text
                    color="text-red-500"
                    text="Bad"
                    typeface={Text.typeface.InterBold}
                  />
                </div>
              </div>
            </>
          }
        </div>

        <div className="
          flex
          items-center
          space-x-1
        ">
          <Button
            className=" backdrop-blur-sm bg-white/10"
            icon={
              <ArrowLeftIcon
                size={16}
              />
            }
            iconPosition={Button.iconPosition.Left}
            text="Go back"
            version={Button.version.SecondaryGreen}
            onClick={onBackClick}
          />
          <Link
            href="/guides"
            passHref
          >
            <a className="no-underline">
              <Button
                className="border border-green-500"
                icon={
                  <LibraryIcon
                    size={16}
                  />
                }
                iconPosition={Button.iconPosition.Left}
                text="Explore more guides"
                version={Button.version.PrimaryGreen}
              />
            </a>
          </Link>
        </div>

      </div>

    </div>
  )
}

export default GuideRating
